import { type ReactElement } from 'react';
import { s } from '@/theme/tokens';
import { Box, LockIcon, Text } from '@/ui/primitives';
import { ProjectBadge } from './ProjectBadge';
import type { ProjectDto } from '@flowqa/shared';

export type ProjectSelectOptionContentProject = Pick<
  ProjectDto,
  'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'
> & {
  locked?: boolean;
  lockReason?: string | null;
};

type ProjectSelectOptionContentProps = {
  project: ProjectSelectOptionContentProject;
  contentGap?: string;
  selectedValue?: boolean;
};

const projectSelectOptionCompactControlHeight = 40;

export const ProjectSelectOptionContent = ({
  contentGap = s('md'),
  project,
  selectedValue = false,
}: ProjectSelectOptionContentProps): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: contentGap,
      minHeight: selectedValue ? projectSelectOptionCompactControlHeight : undefined,
      minWidth: 0,
      width: '100%',
    }}
  >
    {project.badgeEnabled ? (
      <ProjectBadge
        chars={project.badgeChars}
        color={project.badgeColor}
        disabled={project.locked === true}
      />
    ) : null}
    <Box sx={{ minWidth: 0, flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
      <Text
        role="body"
        component={selectedValue ? 'span' : undefined}
        truncate
        sx={{
          display: selectedValue ? 'inline-flex' : 'block',
          alignItems: selectedValue ? 'center' : undefined,
          lineHeight: selectedValue ? 1.4 : undefined,
          transform: selectedValue ? 'translateY(1px)' : undefined,
        }}
      >
        {project.name}
      </Text>
      {project.locked === true && project.lockReason ? (
        <Text role="caption" color="textSecondary" truncate sx={{ display: 'block' }}>
          {project.lockReason}
        </Text>
      ) : null}
    </Box>
    {project.locked === true ? <LockIcon sx={{ fontSize: 16, flexShrink: 0 }} /> : null}
  </Box>
);
